import { useLayoutEffect } from "react";

import { PillButton } from "./components";

interface GreetingScreenProps {
  firstName: string | null | undefined;
  busy: boolean;
  error: string;
  onContinue: () => void;
}

/**
 * Figma 519:6703 — the greeting. The front door of the flow: the
 * kit flyer's QR carries the household's access code, so this is the first
 * screen a homeowner sees. Completing it advances to the guide.
 *
 * The copy deliberately makes two moves. "Everything's already configured for
 * your home" transfers authority and cuts perceived effort — the old copy
 * claimed the opposite ("all the elements you will need to configure"), which
 * read as a warning. "One compartment at a time" puts the box in the copy
 * without a number: how many compartments apply varies per household
 * (`applicable_steps()` drops systems a home doesn't have), so any fixed count
 * would be wrong for someone.
 *
 * First person belongs to the assistant sheet, not here — two voices both
 * saying "I'll guide you" is one guide too many.
 */
export function GreetingScreen({ firstName, busy, error, onContinue }: GreetingScreenProps) {
  // This screen renders no AppBar (there's nowhere to go back to), so it
  // doesn't inherit that component's scroll reset. Cover it directly: it
  // mounts fresh whenever the flow lands here, and the window may still be
  // scrolled from whatever was on screen before. Layout effect so the reset
  // lands before paint, never a frame into it.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="mx-auto flex h-[100dvh] w-full max-w-[390px] flex-col overflow-y-auto bg-white">
      {/* The same sunlit attic as the offboarding hero — the flow opens and
          closes on the same room, before and after. A photo rather than a
          colour block, so the yellow primary CTA below stays the loudest
          thing on the screen.

          The screen is one fixed viewport: the photo flexes to absorb
          whatever height the device has, and the text + CTA below are
          shrink-0, so nothing sits under the fold on a normal phone. On
          short/zoomed viewports the photo gives way down to min-h-40 and the
          root scrolls — the CTA and any error must stay reachable at 200%
          text zoom (Codex review, WCAG 1.4.4/1.4.10). */}
      <img
        src="/brand/onboarding/offboarding-hero.jpg"
        alt="A sunlit attic room with VELUX roof windows"
        className="min-h-40 w-full flex-1 object-cover"
      />
      <div className="flex shrink-0 flex-col justify-between gap-8 px-6 py-5">
        <div className="flex flex-col gap-4">
          <h1 className="text-primary type-title">
            {firstName ? `Hello ${firstName}` : "Hello"}
            <br />
            Let&apos;s set up SmartClimate
          </h1>
          <p className="text-muted text-[17px] leading-6">
            Everything&apos;s already configured for your home. You will be connecting the hardware,
            one compartment of your box at a time.
          </p>
        </div>
        <div className="flex flex-col gap-3 pb-8">
          {error && (
            <p role="alert" className="text-destructive text-center text-[15px]">
              {error}
            </p>
          )}
          <PillButton label="Install the SmartClimate Hub" busy={busy} onClick={onContinue} />
        </div>
      </div>
    </div>
  );
}
